import { useTranslate, useMany } from '@refinedev/core';
import { Card, Flex, Typography } from 'antd';
import {
  EllipsisOutlined,
  HeartOutlined,
  StarOutlined,
} from '@ant-design/icons';
import type {
  PersonListOut,
  PublicationListOut,
  RankingDetailOut,
} from '@/api/generated/schemas';
import {
  GUTTER,
  MOBILE_PLACEHOLDER,
} from '@/components/mobile_preview/constants';
import { MobilePreviewProvider } from '@/components/mobile_preview/MobilePreviewProvider';
import { MobileThemeToggle } from '@/components/mobile_preview/MobileThemeToggle';
import { PhoneFrame } from '@/components/mobile_preview/PhoneFrame';
import { MobilePreviewDisclaimer } from '@/components/mobile_preview/MobilePreviewDisclaimer';
import { useMobilePreview } from '@/components/mobile_preview/mobilePreviewContext';
import {
  formatAudioDuration,
  orderedAuthorIds,
  publicationFormats,
} from '@/components/user_catalog/publications/publicationFacts';
import {
  RANKING_CARD_SURFACE,
  RANKING_MUTED_TEXT,
  RANKING_NUMERAL_FILL,
  RANKING_NUMERAL_STROKE,
} from './rankingPreview';

interface RankingPreviewPanelProps {
  record: RankingDetailOut;
}

const COVER_SIZE = 72;
/** How far the numeral hangs left of the cover. */
const NUMERAL_OVERHANG = 14;

interface RankedCardProps {
  position: number;
  publication?: PublicationListOut;
  authorById: Map<number, PersonListOut>;
}

// App ranking-card replica from admin data alone; per-user state
// (favourites, download/progress, "Oceń") is intentionally absent.
const RankedCard = ({ position, publication, authorById }: RankedCardProps) => {
  const t = useTranslate();
  const { mobileTheme } = useMobilePreview();
  const muted = RANKING_MUTED_TEXT[mobileTheme];

  const authors = [...(publication?.publication_authors ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((credit) => authorById.get(credit.author_id)?.name)
    .filter(Boolean)
    .join(', ');

  const formats = publicationFormats(publication)
    .map((format) => t(`user_catalog.rankings.preview.formats.${format}`))
    .join(', ');

  const duration = formatAudioDuration(
    publication?.audio?.duration_minutes ?? null
  );
  const pagesCount = publication?.ebook?.pages_count;
  // One stats line, app-style: audio length and/or ebook pages.
  const statsLine = [
    duration,
    pagesCount != null && pagesCount > 0
      ? t('user_catalog.rankings.preview.pages', { count: pagesCount })
      : null,
  ]
    .filter(Boolean)
    .join(' • ');
  const rating =
    publication?.total_rating != null && publication.total_rating > 0
      ? publication.total_rating.toFixed(1)
      : null;

  return (
    <div
      style={{
        background: RANKING_CARD_SURFACE[mobileTheme],
        borderRadius: 16,
        padding: 12,
        paddingLeft: 12 + NUMERAL_OVERHANG,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
      }}
    >
      {/* Numeral hangs over the cover's top-left corner, above it. */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        {publication?.cover_url ? (
          <img
            src={publication.cover_url}
            alt=""
            style={{
              width: COVER_SIZE,
              height: COVER_SIZE,
              objectFit: 'cover',
              borderRadius: 12,
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: COVER_SIZE,
              height: COVER_SIZE,
              borderRadius: 12,
              background: MOBILE_PLACEHOLDER[mobileTheme],
            }}
          />
        )}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: -NUMERAL_OVERHANG,
            top: -8,
            fontSize: 42,
            fontWeight: 800,
            lineHeight: 1,
            color: RANKING_NUMERAL_FILL[mobileTheme],
            WebkitTextStroke: `1.5px ${RANKING_NUMERAL_STROKE[mobileTheme]}`,
          }}
        >
          {position}
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 15,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {publication?.title ?? '…'}
        </div>
        {authors && (
          <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>
            {authors}
          </div>
        )}
        {formats && (
          <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>
            {formats}
          </div>
        )}
        {statsLine && (
          <div style={{ fontSize: 12, marginTop: 3 }}>{statsLine}</div>
        )}
      </div>

      <Flex
        vertical
        align="flex-end"
        justify="space-between"
        style={{ alignSelf: 'stretch', flexShrink: 0 }}
      >
        <Flex gap={10} align="center">
          <HeartOutlined style={{ fontSize: 17 }} />
          <EllipsisOutlined style={{ fontSize: 17 }} />
        </Flex>
        {rating && (
          <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
            {rating}
            <StarOutlined style={{ fontSize: 14, marginLeft: 5 }} />
          </span>
        )}
      </Flex>
    </div>
  );
};

/** The ranking screen inside the frame; needs the provider's theme. */
const RankingScreen = ({ record }: RankingPreviewPanelProps) => {
  const t = useTranslate();

  const sorted = [...(record.publication_rankings ?? [])].sort(
    (a, b) => a.position - b.position
  );
  const publicationIds = sorted.map((rp) => rp.publication_id);

  // Same resource+ids as the publications table — shares its query cache.
  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });
  const publicationById = new Map(
    publicationsResult?.data.map((p) => [p.id, p])
  );

  const authorIds = orderedAuthorIds(
    sorted.map((rp) => publicationById.get(rp.publication_id))
  );
  const { result: authorsResult } = useMany<PersonListOut>({
    resource: 'user_catalog.authors',
    ids: authorIds,
    queryOptions: { enabled: authorIds.length > 0 },
  });
  const authorById = new Map(authorsResult?.data.map((a) => [a.id, a]));

  return (
    <>
      <div style={{ padding: `0 ${GUTTER}px` }}>
        <Typography.Title
          level={3}
          style={{ color: 'inherit', margin: '0 0 16px', fontWeight: 700 }}
        >
          {record.name}
        </Typography.Title>

        {sorted.length === 0 ? (
          <div style={{ opacity: 0.6, padding: '24px 0 8px' }}>
            {t('user_catalog.rankings.preview.empty')}
          </div>
        ) : (
          <Flex vertical gap={12}>
            {sorted.map((rp) => (
              <RankedCard
                key={rp.id}
                position={rp.position}
                publication={publicationById.get(rp.publication_id)}
                authorById={authorById}
              />
            ))}
          </Flex>
        )}
      </div>
    </>
  );
};

/** Phone-frame replica of the app's ranking screen. */
export const RankingPreviewPanel = ({ record }: RankingPreviewPanelProps) => {
  const t = useTranslate();

  return (
    <Card title={t('user_catalog.rankings.preview.title')}>
      <MobilePreviewProvider>
        <Flex justify="center" style={{ marginBottom: 16 }}>
          <MobileThemeToggle />
        </Flex>

        <PhoneFrame>
          <RankingScreen record={record} />
        </PhoneFrame>

        <MobilePreviewDisclaimer />
      </MobilePreviewProvider>
    </Card>
  );
};
